import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import clsx from "clsx";
import { forwardRef } from "react";
import { mergeSx } from "../system";
import { BaseComponent } from "./BaseComponent";
export const gridClasses = {
    root: "MinervaGrid-root",
    container: "MinervaGrid-container",
    item: "MinervaGrid-item",
};
export const getGridSizing = (breakpoint) => ({
    width: breakpoint === "auto" ? "auto" : undefined,
    flexGrow: typeof breakpoint === "number"
        ? 0
        : breakpoint === true
            ? 1
            : breakpoint === "auto"
                ? 0
                : undefined,
    flexShrink: breakpoint === "auto" ? 0 : undefined,
    flexBasis: typeof breakpoint === "number"
        ? `${(breakpoint / 12) * 100}%`
        : breakpoint === true
            ? 0
            : breakpoint === "auto"
                ? "auto"
                : undefined,
    maxWidth: typeof breakpoint === "number"
        ? `${(breakpoint / 12) * 100}%`
        : breakpoint === true
            ? "100%"
            : breakpoint === "auto"
                ? "none"
                : undefined,
});
/**
 * The Grid component is a low level div that facilitates easy creation of a flex
 * containers and their flex children. This component exposes common flexbox CSS
 * properties on its root API for ease of use. It extends the BaseComponent to
 * provide a default html tag of div and also exposes the sx prop.
 *
 * @example `<Grid container justifyContent="space-between" gap={2}/>`
 *
 * @returns JSX.Element (jsx import modified to be EmotionJSX.Element)
 */
export const Grid = forwardRef(({ container, useContainerQueries, containerType = container && useContainerQueries ? "inline-size" : undefined, item, xs, sm, md, lg, xl, cxs, csm, cmd, clg, cxl, gap, rowGap, columnGap, spacing, columnSpacing, rowSpacing, display, flexDirection = "row", justifyContent, alignItems, flexWrap, flex, flexBasis, flexGrow, flexShrink, flexFlow, order, alignSelf, justifySelf, height, width, minHeight, minWidth, maxHeight, maxWidth, className, sx, ...props }, ref) => {
    return (_jsx(BaseComponent, { ref: ref, as: "div", className: clsx([
            className,
            gridClasses.root,
            container && gridClasses.container,
            item && gridClasses.item,
        ]), sx: mergeSx({
            boxSizing: "inherit",
            gap,
            rowGap,
            columnGap,
            flexDirection,
            justifyContent,
            alignItems,
            flex,
            flexBasis,
            flexGrow,
            flexShrink,
            flexFlow,
            order,
            alignSelf,
            justifySelf,
            height,
            minHeight,
            minWidth,
            maxHeight,
            maxWidth,
            // CONTAINER STYLES
            containerType,
            width: width
                ? width
                : container && columnSpacing && typeof columnSpacing === "number"
                    ? (theme) => `calc(100% + ${theme.spacing(columnSpacing)})`
                    : container && columnSpacing && typeof columnSpacing === "string"
                        ? `calc(100% - ${columnSpacing})`
                        : container && spacing && typeof spacing === "number"
                            ? (theme) => `calc(100% + ${theme.spacing(spacing)})`
                            : container && spacing && typeof spacing === "string"
                                ? `calc(100% - ${spacing})`
                                : container
                                    ? "100%"
                                    : undefined,
            display: display ? display : container ? "flex" : undefined,
            flexWrap: flexWrap ? flexWrap : container ? "wrap" : undefined,
            // SPACING STYLES
            marginTop: rowSpacing && typeof rowSpacing === "number"
                ? (theme) => theme.spacing(-rowSpacing)
                : rowSpacing && typeof rowSpacing === "string"
                    ? `-${rowSpacing}`
                    : spacing && typeof spacing === "number"
                        ? (theme) => theme.spacing(-spacing)
                        : spacing && typeof spacing === "string"
                            ? `-${spacing}`
                            : undefined,
            marginLeft: columnSpacing && typeof columnSpacing === "number"
                ? (theme) => theme.spacing(-columnSpacing)
                : columnSpacing && typeof columnSpacing === "string"
                    ? `-${columnSpacing}`
                    : spacing && typeof spacing === "number"
                        ? (theme) => theme.spacing(-spacing)
                        : spacing && typeof spacing === "string"
                            ? `-${spacing}`
                            : undefined,
            [`& >.${gridClasses.item}`]: spacing || columnSpacing || rowSpacing
                ? {
                    paddingTop: rowSpacing && typeof rowSpacing === "number"
                        ? (theme) => theme.spacing(rowSpacing)
                        : rowSpacing && typeof rowSpacing === "string"
                            ? `${rowSpacing}`
                            : spacing && typeof spacing === "number"
                                ? (theme) => theme.spacing(spacing)
                                : spacing && typeof spacing === "string"
                                    ? `${spacing}`
                                    : undefined,
                    paddingLeft: columnSpacing && typeof columnSpacing === "number"
                        ? (theme) => theme.spacing(columnSpacing)
                        : columnSpacing && typeof columnSpacing === "string"
                            ? `${columnSpacing}`
                            : spacing && typeof spacing === "number"
                                ? (theme) => theme.spacing(spacing)
                                : spacing && typeof spacing === "string"
                                    ? `${spacing}`
                                    : undefined,
                }
                : undefined,
            // ITEM STYLES
            breakpoints: {
                ...(useContainerQueries
                    ? {
                        cxs: {
                            ...getGridSizing(xs),
                        },
                        csm: {
                            ...getGridSizing(sm),
                        },
                        cmd: {
                            ...getGridSizing(md),
                        },
                        clg: {
                            ...getGridSizing(lg),
                        },
                        cxl: {
                            ...getGridSizing(xl),
                        },
                    }
                    : {
                        xs: {
                            ...getGridSizing(xs),
                        },
                        sm: {
                            ...getGridSizing(sm),
                        },
                        md: {
                            ...getGridSizing(md),
                        },
                        lg: {
                            ...getGridSizing(lg),
                        },
                        xl: {
                            ...getGridSizing(xl),
                        },
                    }),
                cxs: {
                    ...getGridSizing(cxs),
                },
                csm: {
                    ...getGridSizing(csm),
                },
                cmd: {
                    ...getGridSizing(cmd),
                },
                clg: {
                    ...getGridSizing(clg),
                },
                cxl: {
                    ...getGridSizing(cxl),
                },
            },
        }, sx), ...props }));
});
//# sourceMappingURL=Grid.js.map